import useClientRedirect from '@/lib/hooks/routing/useRedirect'
import { useEffect } from 'react'
import { useRouter } from 'next/router'
import useGlobalStore from '@/store/useGlobalStore'
import type { CMSError } from '@/lib/cms/types/CMSError'
import type { CosmicProductType } from '@/lib/cms/cosmic/types/CosmicProductType'
import { mixpanelService } from '@/lib/services/mixpanel/initializeMixPanel'
import { logError } from '@/lib/utils/shared/log'

type Props = {
  products?: CosmicProductType[] | { error: CMSError } | null
}

export const useRedirectForProductShare = ({ products }: Props) => {
  const { clientRedirect } = useClientRedirect()
  const router = useRouter()
  const sku = router.query?.sku
  const referralId =
    router.query?.refId ||
    router.query?.referralId ||
    router.query?.referralCode
  const cart = router.query?.cart
  const { setUser } = useGlobalStore()

  const getProductSlug = (productItems: string) => {
    const [skuItem, quantity] = productItems.split(':')
    if (!products) return ''
    if ('error' in products) return ''

    const product = products.find((productItem) =>
      productItem.variations.find(
        (variation) => variation.pricing?.sku === skuItem,
      ),
    )

    return product
      ? `${product.slug}:${product.variations[0].pricing?.sku}:one-time-purchase:${quantity}`
      : ''
  }

  const buildCartSlug = (cartItems: string) => {
    const skuList = cartItems.split('|')
    const slugList = skuList
      .map((skuItem) => getProductSlug(skuItem))
      .filter(Boolean)
      .map((item, index, array) =>
        index < array.length - 1 ? `${item};` : item,
      )
      .join('')
    return slugList
  }

  const setReferralProperties = () => {
    const referralProps: Record<string, string> = {}

    if (router.query.refId || router.query.referralId) {
      referralProps.referral_id =
        (router.query.refId as string) || (router.query.referralId as string)
    }

    if (router.query.referralCode) {
      referralProps.referral_code = router.query.referralCode as string
    }

    if (Object.keys(referralProps).length > 0) {
      mixpanelService.registerSuperProperties(referralProps)
    }

    setUser({ refID: referralId as string })
  }

  const getRedirectUrl = () => {
    if (cart) {
      const listSlug = buildCartSlug(cart as string)
      return `/c/${referralId}/cart/${listSlug}`
    }

    if (sku && products && !('error' in products)) {
      const matchedItem = products.find((product) =>
        product.variations.find((variation) => variation.pricing?.sku === sku),
      )

      if (matchedItem) {
        return `/product/${matchedItem.slug}?sku=${sku}`
      }
    }

    return '/'
  }

  useEffect(() => {
    if (!products) return
    if (!cart && !sku) return
    if ('error' in products) return

    const redirectUrl = getRedirectUrl()
    setReferralProperties()

    const timeoutId = setTimeout(() => {
      clientRedirect.to({ path: redirectUrl }).catch((error) => {
        logError('Error during redirect:', error)
      })
    }, 50)

    // eslint-disable-next-line unicorn/no-abusive-eslint-disable, @typescript-eslint/no-misused-promises, consistent-return
    return () => clearTimeout(timeoutId)
  }, [products, cart, sku])
}
